/**
 * `addedAtByline` — the relative-time suffix for a list item's "Added by …"
 * byline, in the calm calendar-day tone the Planning + Shopping prototype uses:
 *
 *   today · yesterday · 2 days ago · … · 6 days ago · 1 week ago · N weeks ago
 *
 * This is deliberately NOT the admin compact idiom ("4h ago", "just now") — the
 * lists surface speaks in whole days, matching `Added by Sam · 2 days ago`.
 *
 * Returns `null` for a missing or unparseable timestamp so callers omit the
 * suffix entirely — never render `· Invalid Date` / `· NaN`.
 */
export function addedAtRelative(iso: string | null | undefined, now: Date = new Date()): string | null {
  if (!iso) return null;
  const then = new Date(iso);
  const thenMs = then.getTime();
  if (Number.isNaN(thenMs)) return null;

  // Day granularity is calendar-based: compare local midnights so an item added
  // late yesterday reads "yesterday", not "1 day ago" only after 24h elapse.
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(now) - startOfDay(then)) / 86_400_000);

  // A future or same-day timestamp reads as "today" (clock skew shouldn't ever
  // surface "in 2 days" on a byline).
  if (dayDiff <= 0) return 'today';
  if (dayDiff === 1) return 'yesterday';
  if (dayDiff < 7) return `${dayDiff} days ago`;

  const weeks = Math.floor(dayDiff / 7);
  return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
}
